import React, { useEffect, useState } from 'react';
import { Trash2, FileSpreadsheet, FileText, Edit, Eye, Loader2, Download, Truck } from 'lucide-react';
import { medicaoService } from '../../services/medicaoService';
import { CadastroOSModal } from '../../pages/CadastroOSModal';
import { VisualizarOSModal } from '../../pages/VisualizarOSModal';
import { exportMedicaoToExcel, exportMedicaoToPdf, resolveTipoVeiculoDisplay } from '../../services/medicaoExportService';
import { formatDateBR } from '../../utils/dateUtils';
import { fixEncodingIssues } from '../../utils/textUtils';

interface MedicaoOSListProps {
  medicaoId: number;
  type: 'cliente' | 'agregado';
  onUpdate?: () => void;
  clienteNome?: string;
  agregadoNome?: string;
  periodoInicial?: string;
  periodoFinal?: string;
  valorTotal?: number;
  statusNome?: string;
}

export function MedicaoOSList({
  medicaoId,
  type,
  onUpdate,
  clienteNome,
  agregadoNome,
  periodoInicial,
  periodoFinal,
  valorTotal,
  statusNome,
}: MedicaoOSListProps) {
  const [osList, setOsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [editingOsId, setEditingOsId] = useState<number | null>(null);
  const [viewingOsId, setViewingOsId] = useState<number | null>(null);

  const fetchOS = async () => {
    try {
      setLoading(true);
      const data = type === 'agregado' 
        ? await medicaoService.getOSByMedicaoAgregado(medicaoId)
        : await medicaoService.getOSByMedicaoCliente(medicaoId);
      setOsList(data || []);
    } catch (error) {
      console.error('Error fetching OS list:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (medicaoId) fetchOS();
  }, [medicaoId, type]);

  const handleRemove = async (osId: number) => {
    if (!window.confirm('Deseja remover esta O.S da medição?')) return;
    try {
      if (type === 'agregado') {
        await medicaoService.removeOSFromMedicaoAgregado(osId, medicaoId);
      } else {
        await medicaoService.removeOSFromMedicaoCliente(osId, medicaoId);
      }
      fetchOS();
      if (onUpdate) onUpdate();
    } catch (error: any) {
      alert('Erro ao remover O.S: ' + error.message);
    }
  };

  const getNormalizedExportList = () => {
    const isAgregado = type === 'agregado';
    return osList.map((os) => {
      const p = os.pedido?.pessoa;
      const pessoaObj = Array.isArray(p) ? p[0] : p;
      const cNome = pessoaObj?.nome || os.cliente_nome || os.pessoa?.nome || clienteNome || '-';
      const aNome = os.agregado_nome || os.agregado?.nome || os.veiculos?.motorista?.nome || agregadoNome || '-';

      const qtdAjud = Number(os.quantidade_ajudante ?? os.qtd_ajudante ?? 0);
      const valAjud = isAgregado
        ? Number(os.ajudante_repasse || 0)
        : Number(os.subtotal_viagem ?? (Number(os.valor_unit_ajud || 0) * qtdAjud) ?? 0);

      const totalVal = isAgregado ? Number(os.valor_total_repasses || 0) : Number(os.total_os || 0);
      const tipoVeiculo = resolveTipoVeiculoDisplay(os);
      const valUnitVeic = Number(
        os.valor_unit_servi ??
        (qtdAjud > 0 ? (totalVal - valAjud) : totalVal) ??
        0
      );

      return {
        id: os.id,
        data_atendimento: os.data_atendimento,
        cliente_nome: cNome,
        agregado_nome: aNome,
        motorista_nome: os.veiculos?.motorista?.nome || os.motorista?.nome || os.motorista_nome || '-',
        placa: os.veiculos?.placa || os.placa || '-',
        tipo_veiculo: tipoVeiculo,
        descricao: os.descricao ? fixEncodingIssues(os.descricao) : '',
        valor_unit_servi: valUnitVeic,
        retirada: os.retirada || '',
        entrega: os.entrega || '',
        horario_inicio: os.horario_inicio || '',
        horario_final: os.horario_final || '',
        hora_total: os.hora_total || '',
        quantidade_ajudante: qtdAjud,
        valor_ajudante: valAjud,
        total_os: Number(os.total_os) || 0,
        valor_total_repasses: Number(os.valor_total_repasses) || 0,
        status_nome: os.status?.status || os.status_nome || '',
      };
    });
  };

  const handleExportExcel = () => {
    try {
      if (osList.length === 0) {
        alert('Não há Ordens de Serviço vinculadas para exportar.');
        return;
      }

      const exportList = getNormalizedExportList();
      const calculatedTotal = exportList.reduce(
        (sum, item) => sum + (type === 'agregado' ? Number(item.valor_total_repasses || 0) : Number(item.total_os || 0)),
        0
      );

      exportMedicaoToExcel(
        {
          id: medicaoId,
          type,
          clienteNome: clienteNome || exportList[0]?.cliente_nome,
          agregadoNome: agregadoNome || exportList[0]?.agregado_nome,
          periodoInicial,
          periodoFinal,
          valorTotal: valorTotal || calculatedTotal,
          statusNome,
        },
        exportList
      );
    } catch (err: any) {
      console.error('Erro ao exportar medição para Excel:', err);
      alert('Erro ao gerar o arquivo Excel da medição: ' + (err?.message || 'Tente novamente.'));
    }
  };

  const handleExportPdf = async () => {
    if (osList.length === 0) {
      alert('Não há Ordens de Serviço vinculadas para exportar.');
      return;
    }

    try {
      setExportingPdf(true);
      const exportList = getNormalizedExportList();
      const calculatedTotal = exportList.reduce(
        (sum, item) => sum + (type === 'agregado' ? item.valor_total_repasses : item.total_os),
        0
      );

      await exportMedicaoToPdf(
        {
          id: medicaoId,
          type,
          clienteNome: clienteNome || exportList[0]?.cliente_nome,
          agregadoNome: agregadoNome || exportList[0]?.agregado_nome,
          periodoInicial,
          periodoFinal,
          valorTotal: valorTotal || calculatedTotal,
          statusNome,
        },
        exportList
      );
    } catch (err) {
      console.error('Erro ao gerar PDF da medição:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) return <div className="p-4 text-center text-slate-500">Carregando Ordens de Serviço...</div>;

  return (
    <div className="mt-6 border border-slate-200 rounded-lg overflow-hidden shadow-xs">
      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex flex-wrap justify-between items-center gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-slate-800">Ordens de Serviço Compostas</h3>
          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-semibold">
            {osList.length}
          </span>
        </div>

        {/* Botões de Exportação Excel e PDF */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={osList.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-emerald-50 text-emerald-700 border border-emerald-300 hover:border-emerald-400 rounded-lg text-xs font-bold transition-all shadow-2xs disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            title="Exportar tabela de O.S. para Excel (XLS)"
          >
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
            <span>Excel</span>
          </button>

          <button
            type="button"
            onClick={handleExportPdf}
            disabled={osList.length === 0 || exportingPdf}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-red-50 text-red-700 border border-red-300 hover:border-red-400 rounded-lg text-xs font-bold transition-all shadow-2xs disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            title="Exportar relatório da medição em PDF"
          >
            {exportingPdf ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                <span>Gerando...</span>
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 text-red-600" />
                <span>PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">O.S</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">
                {type === 'agregado' ? 'Agregado' : 'Cliente'}
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Data</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase">Tipo Veículo</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Placa</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Descrição</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Valor Unit.</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Total O.S.</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Ação</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {osList.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-4 text-center text-sm text-slate-500">Nenhuma O.S vinculada.</td>
              </tr>
            ) : (
              osList.map((os) => {
                const tipoV = resolveTipoVeiculoDisplay(os);
                const qtdAjud = Number(os.quantidade_ajudante ?? os.qtd_ajudante ?? 0);
                const valAjud = type === 'agregado'
                  ? Number(os.ajudante_repasse || 0)
                  : Number(os.subtotal_viagem ?? (Number(os.valor_unit_ajud || 0) * qtdAjud) ?? 0);
                const totalOsVal = type === 'agregado' ? Number(os.valor_total_repasses || 0) : Number(os.total_os || 0);
                const valUnitVeic = Number(
                  os.valor_unit_servi ??
                  (qtdAjud > 0 ? (totalOsVal - valAjud) : totalOsVal) ??
                  0
                );
                const descClean = os.descricao ? fixEncodingIssues(os.descricao) : '-';

                return (
                  <tr key={os.id} className="hover:bg-slate-50 text-xs">
                    <td className="px-3 py-2 font-bold text-slate-900">{os.id}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[160px] truncate" title={type === 'agregado' ? (os.agregado_nome || os.agregado?.nome || '-') : (os.pedido?.pessoa?.nome || os.cliente_nome || '-')}>
                      {type === 'agregado' ? (
                        <span className="font-medium text-slate-800">
                          {os.agregado_nome || os.agregado?.nome || os.veiculos?.motorista?.nome || '-'}
                        </span>
                      ) : (
                        (() => {
                          const p = os.pedido?.pessoa;
                          const pessoaObj = Array.isArray(p) ? p[0] : p;
                          return pessoaObj?.nome || os.cliente_nome || os.pessoa?.nome || '-';
                        })()
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{formatDateBR(os.data_atendimento)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-200">
                        {tipoV}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{os.veiculos?.placa || os.placa || '-'}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={descClean}>
                      {descClean}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valUnitVeic)}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-slate-900 whitespace-nowrap">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalOsVal)}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setViewingOsId(os.id)}
                        className="text-slate-600 hover:text-slate-900 p-1 rounded hover:bg-slate-100 cursor-pointer"
                        title="Visualizar O.S"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingOsId(os.id)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 cursor-pointer"
                        title="Editar Ordem de Serviço"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        type="button"
                        onClick={() => handleRemove(os.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 cursor-pointer"
                        title="Remover da Medição"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editingOsId && (
        <CadastroOSModal
          osId={editingOsId}
          isOpen={!!editingOsId}
          onClose={() => setEditingOsId(null)}
          onSaved={() => {
            fetchOS();
            if (onUpdate) onUpdate();
            setEditingOsId(null);
          }}
        />
      )}

      {viewingOsId && (
        <VisualizarOSModal
          osId={viewingOsId}
          onClose={() => setViewingOsId(null)}
        />
      )}
    </div>
  );
}

